import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { Cancel, Close, Done } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useCreateOrUpdateRealEstateEvent } from '../hooks';
import { IRealEstateEvent, IRealEstateEventType } from '../interfaces';

interface IProps extends Omit<DialogProps, 'onClose'> {
  realEstateId: string;
  realEstateEvent?: IRealEstateEvent;
  realEstateEventTypes: IRealEstateEventType[];
  onClose(): void;
  onCreateOrUpdate?(realEstateEvent: IRealEstateEvent): void;
}

const RealEstateEventDialog = ({
  realEstateId,
  realEstateEvent,
  realEstateEventTypes,
  onClose,
  onCreateOrUpdate,
  ...props
}: IProps) => {
  const initialValues: IRealEstateEvent = {
    active: true,
    comment: '',
    eventEndTime: '',
    eventStartTime: '',
    realEstateEventType: null,
  };

  const validationSchema = yup.object().shape({
    active: yup.boolean(),
    comment: yup.string(),
    eventEndTime: yup.string().required('Обязательное поле'),
    eventStartTime: yup.string().required('Обязательное поле'),
    realEstateEventType: yup.object().nullable().required('Обязательное поле'),
  });

  const { createOrUpdateRealEstateEventState, handleCreateOrUpdateRealEstateEvent } = useCreateOrUpdateRealEstateEvent(
    realEstateId,
    realEstateEvent ?? null,
    (realEstateEventData) => {
      onClose();
      if (onCreateOrUpdate) onCreateOrUpdate(realEstateEventData);
    },
  );

  return (
    <Dialog {...props} onClose={onClose} fullWidth maxWidth="sm" scroll="body">
      <DialogTitle>
        <b>Добавить событие</b>
      </DialogTitle>

      <Formik
        initialValues={realEstateEvent ?? initialValues}
        validationSchema={validationSchema}
        onSubmit={handleCreateOrUpdateRealEstateEvent}
        enableReinitialize
      >
        {({ values, touched, errors, setFieldValue, handleChange }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    value={values.realEstateEventType}
                    options={realEstateEventTypes}
                    disabled={createOrUpdateRealEstateEventState.loading}
                    getOptionLabel={(option) => String(option.realEstateEventTypeName)}
                    getOptionSelected={(option, value) => option.realEstateEventTypeId === value.realEstateEventTypeId}
                    onChange={(event, option) => setFieldValue('realEstateEventType', option)}
                    renderInput={(params) => (
                      <TextField
                        label="Тип события"
                        variant="filled"
                        error={Boolean(touched.realEstateEventType) && Boolean(errors.realEstateEventType)}
                        helperText={
                          (Boolean(touched.realEstateEventType) && errors.realEstateEventType) ||
                          'Внимание! Данное примечание отправляется клиенту'
                        }
                        {...params}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="comment"
                    label="Примечание"
                    variant="filled"
                    value={values.comment}
                    onChange={handleChange}
                    fullWidth
                    disabled={createOrUpdateRealEstateEventState.loading}
                    error={Boolean(touched.comment) && Boolean(errors.comment)}
                    helperText={Boolean(touched.comment) && errors.comment}
                  />
                </Grid>

                {values.realEstateEventType && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex' }}>
                      {values.descKz && (
                        <Box sx={{ mr: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {values.descKz}
                          </Typography>
                        </Box>
                      )}

                      {values.descRu && (
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {values.descRu}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid item lg={6} xs={12}>
                  <TextField
                    name="eventStartTime"
                    label="Дата начала"
                    type="datetime-local"
                    variant="filled"
                    value={values.eventStartTime}
                    onChange={handleChange}
                    inputProps={{
                      min: format(new Date(), 'yyyy-MM-dd HH:mm'),
                      max: values.eventEndTime ? format(new Date(values.eventEndTime), 'yyyy-MM-dd HH:mm') : null,
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton size="small" onClick={() => setFieldValue('eventStartTime', '')}>
                            <Close />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled={createOrUpdateRealEstateEventState.loading}
                    error={Boolean(touched.eventStartTime) && Boolean(errors.eventStartTime)}
                    helperText={Boolean(touched.eventStartTime) && errors.eventStartTime}
                  />
                </Grid>

                <Grid item lg={6} xs={12}>
                  <TextField
                    name="eventEndTime"
                    label="Дата окончания"
                    type="datetime-local"
                    variant="filled"
                    value={values.eventEndTime}
                    onChange={handleChange}
                    inputProps={{
                      min: format(
                        values.eventStartTime ? new Date(values.eventStartTime) : new Date(),
                        'yyyy-MM-dd HH:mm',
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton size="small" onClick={() => setFieldValue('eventEndTime', '')}>
                            <Close />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled={createOrUpdateRealEstateEventState.loading}
                    error={Boolean(touched.eventEndTime) && Boolean(errors.eventEndTime)}
                    helperText={Boolean(touched.eventEndTime) && errors.eventEndTime}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="contained"
                disabled={createOrUpdateRealEstateEventState.loading}
                onClick={onClose}
                startIcon={<Cancel />}
              >
                Отменить
              </Button>

              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={createOrUpdateRealEstateEventState.loading}
                startIcon={<Done />}
              >
                Сохранить
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default RealEstateEventDialog;
